import React from 'react';

import { FormField, FormHelp, Input, Label, Spacer, Text, Textarea } from '@zillow/constellation';

import OptionSelector from '../Utilities/OptionSelector';
import HeaderToggle from '../Utilities/HeaderToggle';
import { isMultiFamily } from '../Utilities/MultiFamilyLandlordUtility';
import type { Section } from '../../../../models/index';
import type { HTMLElementEvent } from '../../../../models/Types';
import { NATURAL_GAS } from '../../../../constants';
import SwitchWithTitle from '../../../Widgets/SwitchWithTitle';
import { CenteredHR } from '../../../Widgets/CenteredHR';
import YesAndNo from '../../../Widgets/YesAndNo';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

export default function NaturalGas_v3({ section, setSectionValues }: Props): JSX.Element {
  const isMultiFamilyLandlord = isMultiFamily(section);
  const naturalGas_isEnabled = section.getFieldValue(NATURAL_GAS.FIELD_NAME.IS_ENABLED);
  const tenantSelected = section.getFieldValue(NATURAL_GAS.FIELD_NAME.TENANT_RESPONSIBILITY);
  const landlordSelected = section.getFieldValue(NATURAL_GAS.FIELD_NAME.LANDLORD_RESPONSIBILITY);
  const naturalGas_tenantPaysInAdditionToRent = section.getFieldValue(
    NATURAL_GAS.FIELD_NAME.TENANT_PAYS_IN_ADDITION_TO_RENT,
  );

  function setLandlordResponsibility(): void {
    setSectionValues({
      [NATURAL_GAS.FIELD_NAME.TENANT_RESPONSIBILITY]: false,
      [NATURAL_GAS.FIELD_NAME.LANDLORD_RESPONSIBILITY]: true,
      [NATURAL_GAS.FIELD_NAME.MULTIFAMILY_TENANT_RESPONSIBILITY.DELIVERED_TO_AREAS_OTHER_THAN_TENANTS_UNIT]: null,
    });
  }

  function setTenantResponsibility(): void {
    setSectionValues({
      [NATURAL_GAS.FIELD_NAME.TENANT_RESPONSIBILITY]: true,
      [NATURAL_GAS.FIELD_NAME.LANDLORD_RESPONSIBILITY]: false,
      [NATURAL_GAS.FIELD_NAME.MULTIFAMILY_LANDLORD_RESPONSIBILITY.BILL_AMOUNT]: '',
    });
  }

  function setServiceProvider(event: HTMLElementEvent<typeof Text>): void {
    setSectionValues({
      [NATURAL_GAS.FIELD_NAME.SERVICE_PROVIDER]: event.currentTarget.value,
    });
  }

  function setBillAmount(event: HTMLElementEvent<typeof Text>): void {
    setSectionValues({
      [NATURAL_GAS.FIELD_NAME.MULTIFAMILY_LANDLORD_RESPONSIBILITY.BILL_AMOUNT]: event.currentTarget.value,
    });
  }

  function toggleUtility(): void {
    setSectionValues({
      [NATURAL_GAS.FIELD_NAME.IS_ENABLED]: !section.getFieldValue(NATURAL_GAS.FIELD_NAME.IS_ENABLED),
    });
  }

  function togglePaysInAddition(): void {
    setSectionValues({
      [NATURAL_GAS.FIELD_NAME.TENANT_PAYS_IN_ADDITION_TO_RENT]: !section.getFieldValue(
        NATURAL_GAS.FIELD_NAME.TENANT_PAYS_IN_ADDITION_TO_RENT,
      ),
      [NATURAL_GAS.FIELD_NAME.TENANT_RESPONSIBILITY]: false,
      [NATURAL_GAS.FIELD_NAME.LANDLORD_RESPONSIBILITY]: false,
    });
  }

  function yesDeliveredToAreas(): void {
    setSectionValues({
      [NATURAL_GAS.FIELD_NAME.MULTIFAMILY_TENANT_RESPONSIBILITY.DELIVERED_TO_AREAS_OTHER_THAN_TENANTS_UNIT]: true,
    });
  }

  function noDeliveredToAreas(): void {
    setSectionValues({
      [NATURAL_GAS.FIELD_NAME.MULTIFAMILY_TENANT_RESPONSIBILITY.DELIVERED_TO_AREAS_OTHER_THAN_TENANTS_UNIT]: false,
    });
  }

  return (
    <>
      <HeaderToggle
        header="Natural gas"
        subheader="Applies to this property"
        switchOn={naturalGas_isEnabled}
        onSwitchToggle={toggleUtility}
      />

      {naturalGas_isEnabled && (
        <Spacer marginBottom="sm">
          <CenteredHR />
          <SwitchWithTitle
            text="Tenant pays in addition to base rent"
            onClick={togglePaysInAddition}
            toggled={naturalGas_tenantPaysInAdditionToRent}
          />
        </Spacer>
      )}

      {naturalGas_tenantPaysInAdditionToRent && (
        <>
          <Spacer marginBottom="sm">
            <OptionSelector
              disabled={!naturalGas_isEnabled}
              optionsText="Who will be responsible for natural gas set up?"
              option1Content="Responsible for account set up and direct payments to specified service provider."
              option2Content="Responsible for account set up and will bill tenant separately."
              option1Selected={tenantSelected}
              onOption1Click={setTenantResponsibility}
              option2Selected={landlordSelected}
              onOption2Click={setLandlordResponsibility}
            />
          </Spacer>
          {tenantSelected && (
            <>
              <FormField>
                <Label id="service-provider">What service provider?</Label>
                <Input
                  aria-labelledby="service-provider"
                  placeholder="(example: National Natural Gas Co.)"
                  value={section.getFieldValue(NATURAL_GAS.FIELD_NAME.SERVICE_PROVIDER)}
                  onChange={setServiceProvider}
                />
              </FormField>
              {isMultiFamilyLandlord && <Spacer marginBottom="sm" />}
            </>
          )}

          {isMultiFamilyLandlord && tenantSelected && (
            <>
              <Text>Will this natural gas service be delivered to areas other than the tenant’s unit?</Text>
              <YesAndNo
                yesActive={section.getFieldValue(
                  NATURAL_GAS.FIELD_NAME.MULTIFAMILY_TENANT_RESPONSIBILITY.DELIVERED_TO_AREAS_OTHER_THAN_TENANTS_UNIT,
                )}
                yesHandler={yesDeliveredToAreas}
                noHandler={noDeliveredToAreas}
              />
            </>
          )}

          {isMultiFamilyLandlord && landlordSelected && (
            <FormField
              label={
                <Label id="bill-amount">How will you determine the amount to bill the tenant for natural gas?</Label>
              }
              control={
                <Textarea
                  aria-labelledby="bill-amount"
                  resize="vertical"
                  onChange={setBillAmount}
                  value={section.getFieldValue(NATURAL_GAS.FIELD_NAME.MULTIFAMILY_LANDLORD_RESPONSIBILITY.BILL_AMOUNT)}
                />
              }
              description={
                <FormHelp>
                  Example: The provider bills me per therm used by the entire building. I distribute that cost evenly
                  among each of the building’s 5 units. Each unit pays 1/5 of the total cost.
                </FormHelp>
              }
            />
          )}
        </>
      )}
    </>
  );
}
